import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import en from "./locales/en";

export type Locale = "en" | "ar" | "ku" | "ms" | "zh";

export const LOCALE_STORAGE_KEY = "oelox.pos.locale";

export const localeMeta: Record<Locale, { nativeName: string; englishName: string; dir: "ltr" | "rtl" }> = {
  en: { nativeName: "English", englishName: "English", dir: "ltr" },
  ar: { nativeName: "العربية", englishName: "Arabic", dir: "rtl" },
  ku: { nativeName: "کوردی", englishName: "Kurdish", dir: "rtl" },
  ms: { nativeName: "Melayu", englishName: "Malay", dir: "ltr" },
  zh: { nativeName: "中文", englishName: "Chinese", dir: "ltr" },
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const dictionaries: Partial<Record<Locale, any>> = { en };

function loadLocale(locale: Locale) {
  if (locale === "en") return;
  import(`./locales/${locale}.ts`).then((m) => { dictionaries[locale] = m.default ?? m; });
}
/* eslint-enable @typescript-eslint/no-explicit-any */

function getNested(obj: any, path: string): string {
  const v = path.split(".").reduce((o, k) => o?.[k], obj);
  return typeof v === "string" ? v : path;
}

function readInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  const url = new URLSearchParams(window.location.search).get("lang");
  if (url && url in localeMeta) return url as Locale;
  const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (saved && saved in localeMeta) return saved as Locale;
  return "en";
}

interface Ctx {
  locale: Locale;
  direction: "ltr" | "rtl";
  setLocale: (l: Locale) => void;
  t: (key: string) => string;
}

const C = createContext<Ctx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);
  const dir = localeMeta[locale].dir;

  useEffect(() => { loadLocale(locale); }, [locale]);

  const setLocale = (l: Locale) => {
    setLocaleState(l);
    localStorage.setItem(LOCALE_STORAGE_KEY, l);
  };

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    document.documentElement.setAttribute("data-locale", locale);
    document.body.dir = dir;
  }, [locale, dir]);

  const v = useMemo<Ctx>(() => ({
    locale, direction: dir, setLocale,
    t: (key: string) => {
      const local = getNested(dictionaries[locale], key);
      if (local !== key) return local;
      return getNested(dictionaries.en, key);
    },
  }), [locale, dir]);

  return <C.Provider value={v}>{children}</C.Provider>;
}

export function useI18n() { const c = useContext(C); if (!c) throw new Error("useI18n() must be inside I18nProvider"); return c; }
